/** @jsxRuntime classic */
/** @jsx jsx */

import React from 'react';
import { Link } from 'components/Link/Link';
import { labelStyles } from '@zapier/style-encapsulation';
import { css, jsx } from '@emotion/core';
import { Animation, Colors, Typography, Icon } from '@zapier/design-system';
import styled from '@emotion/styled';

type CollapsibleMenuProps = {
  title: string;
  onChildClick: (e: React.MouseEvent) => void;
  isOpen: boolean;
  onClick: () => void;
  submenu: Array<{
    href: string;
    text: string;
  }>;
};

export const CollapsibleMenu: React.FC<CollapsibleMenuProps> = ({
  title,
  submenu,
  onChildClick,
  isOpen,
  ...otherProps
}) => {
  return (
    <details open={isOpen} {...otherProps}>
      <summary css={Styles.summary}>
        <div css={Styles.summaryInner}>
          <span>{title}</span>
          <span css={Styles.caret(isOpen)}>
            <Icon isBlock={true} name="arrowSmallDown" size={20} />
          </span>
        </div>
      </summary>
      <ul css={Styles.list}>
        {submenu.map((submenuItem) => (
          <li key={submenuItem.href}>
            <LinkWrapper>
              <Link onClick={onChildClick} href={submenuItem.href}>
                <div css={Styles.linkInner}>
                  <p css={Styles.linkTitle}>{submenuItem.text}</p>
                </div>
              </Link>
            </LinkWrapper>
          </li>
        ))}
      </ul>
    </details>
  );
};

const Styles = labelStyles('UniversalSidebarSubmenu', {
  summary: [
    css`
      display: block;
      border-radius: 3px;
      color: ${Colors.neutral700};
      margin-bottom: 0;

      &:hover,
      &:focus {
        color: ${Colors.neutral800};
        background-color: ${Colors.neutral200};
      }

      [open] > & {
        color: ${Colors.blueJeans};
        background-color: ${Colors.acidWash};
        margin-bottom: 0;

        div {
          font-weight: 700;
        }
      }
    `,
    css`
      /* Hides default icon on details element */
      &::-webkit-details-marker {
        display: none;
      }
      /* Hide default icon from IE. */
      &::before {
        display: none;
      }
    `,
  ],

  // Wrapper so layout works in Safari.
  summaryInner: [
    css`
      ${Typography.base};
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;
      cursor: pointer;
      display: grid;
      grid-template-columns: 1fr auto;
      gap: 20px;
      align-items: center;
      min-height: 40px;
      padding: 10px;
      white-space: nowrap;
      width: 100%;
      transition: ${Animation.transitionValue};
    `,
  ],
  caret: (isOpen: boolean) => [
    css`
      transition: ${Animation.transitionValue};
    `,
    isOpen &&
      css`
        transform: rotate(-180deg);
      `,
  ],

  list: [
    css`
      display: grid;
      grid-template-columns: 100%;
      gap: 5px;
      padding: 10px;
      border-bottom: 1px solid ${Colors.neutral400};
    `,
    css`
      margin-bottom: 10px;
    `,
  ],
  linkInner: css`
    transform: translateY(1px);
  `,

  linkTitle: [
    css`
      font-weight: 500;
      padding: 10px;
    `,
  ],
});

const LinkWrapper = styled.div`
  display: block;
  border-radius: 3px;
  color: ${Colors.neutral700};
  line-height: 20px;
  font-size: 14px;

  &:hover,
  &:focus {
    color: ${Colors.blueJeans};
    background-color: ${Colors.acidWash};
  }
`;
